import { computed, type ComputedRef } from "vue";
import { useQuery } from "@vue/apollo-composable";
import dayjs from "dayjs";
import { GET_PLANNING } from "@/graphql/planning/queries";
import { GET_USER } from "@/graphql/user/queries";
import { normalizePlanningUser } from "@/helpers/planning/planningNormalizers";
import {
  normalizeProjects,
  normalizePlannedTimes,
  normalizeTimeOffs,
  normalizeHolidaysByUser,
  normalizeTimesheetEntries,
  type PlanningPlannedTime,
  type PlanningTimeOff,
  type PlanningHoliday,
  type PlanningTimesheetEntry,
} from "@/helpers/planning/planningDataNormalizers";
import type {
  PlanningProject,
  PlanningUser,
  VisibleDay,
} from "@/views/planning/type";

/**
 * Composable that fetches planning data scoped to **today** and the **current user**.
 *
 * Optionally filter by project IDs. When `projectIds` is provided and non-empty,
 * only planned times and timesheet entries belonging to those projects are returned.
 *
 * Returns all the props needed to render a single-day `PlanningCalendar` for
 * the logged-in employee.
 */
export function useTodayPlanning(options?: {
  projectIds?: ComputedRef<string[]>;
}) {
  const today = dayjs().format("YYYY-MM-DD");

  // Fetch current user
  const { result: userResult } = useQuery(GET_USER);
  const currentUserId = computed(() => userResult.value?.user?.id ?? null);

  // Fetch planning data for today only
  const { result: planningResult, refetch } = useQuery(
    GET_PLANNING,
    { startDate: today, endDate: today },
    { fetchPolicy: "network-only" },
  );

  // --- Current user as PlanningUser ---
  const currentUser = computed<PlanningUser | null>(() => {
    if (!currentUserId.value) return null;
    const rawUsers = planningResult.value?.planning?.users ?? [];
    const matched = rawUsers.find(
      (u: { id: string }) => u.id === currentUserId.value,
    );
    return matched ? normalizePlanningUser(matched) : null;
  });

  // --- Users: only the current user ---
  const users = computed<PlanningUser[]>(() => {
    return currentUser.value ? [currentUser.value] : [];
  });

  // --- Visible days: single day (today) ---
  const visibleDays = computed<VisibleDay[]>(() => {
    const d = dayjs(today);
    return [
      {
        date: today,
        dayName: d.format("ddd"),
        dayDate: d.date(),
        dayOfWeek: d.day(),
        monthLabel: d.format("MMM"),
      },
    ];
  });

  // --- Helper: check if project filter is active ---
  const hasProjectFilter = computed(() => {
    const ids = options?.projectIds?.value;
    return ids != null && ids.length > 0;
  });

  const matchesProjectFilter = (projectId: string | null | undefined) => {
    if (!hasProjectFilter.value) return true;
    if (!projectId) return false;
    return options!.projectIds!.value.includes(projectId);
  };

  // --- Projects: filtered by selected project IDs ---
  const projects = computed<PlanningProject[]>(() => {
    const all = normalizeProjects(
      planningResult.value?.planning?.projects ?? [],
    );
    if (!hasProjectFilter.value) return all;
    return all.filter((p) => options!.projectIds!.value.includes(p.id));
  });

  // --- Planned times: filtered to current user + project ---
  const plannedTimes = computed<PlanningPlannedTime[]>(() => {
    const all = normalizePlannedTimes(
      planningResult.value?.planning?.plannedTimes ?? [],
    );
    return all.filter(
      (pt) =>
        pt.user?.id === currentUserId.value &&
        matchesProjectFilter(pt.project?.id),
    );
  });

  // --- Time-offs: filtered to current user ---
  const timeOffs = computed<PlanningTimeOff[]>(() => {
    const all = normalizeTimeOffs(
      planningResult.value?.planning?.timeOffs ?? [],
    );
    return all.filter((to) => to.user?.id === currentUserId.value);
  });

  // --- Holidays: filtered to current user ---
  const holidays = computed<
    Array<{ user: PlanningUser; holidays: PlanningHoliday[] }>
  >(() => {
    if (!currentUser.value) return [];
    const allHolidays = normalizeHolidaysByUser(
      planningResult.value?.planning?.userHolidaysMap ?? [],
    );
    const userHolidays = allHolidays[currentUserId.value!] ?? [];
    return [{ user: currentUser.value, holidays: userHolidays }];
  });

  // --- Timesheet entries: filtered to current user + project ---
  const timesheetEntries = computed<PlanningTimesheetEntry[]>(() => {
    const all = normalizeTimesheetEntries(
      planningResult.value?.planning?.timesheetEntries ?? [],
    );
    return all.filter(
      (entry) =>
        entry.user?.id === currentUserId.value &&
        matchesProjectFilter(entry.project?.id),
    );
  });

  return {
    users,
    visibleDays,
    plannedTimes,
    timeOffs,
    holidays,
    projects,
    timesheetEntries,
    refetch,
  };
}
